import { defineStore, storeToRefs } from 'pinia'
import { useDashboardStore } from '@/presentation/stores/dashboard/dashboard.ts'
import { useDashboardClassroomStore } from '@/presentation/stores/dashboard/dashboardClassroom.ts'
import { computed, ref, type Ref } from 'vue'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'

import { FetchDashboardClassroomScoreModuleResultsUseCase } from '@/core/useCases/dashboard/FetchDashboardClassroomScoreModuleResultsUseCase.ts'
import { FetchDashboardClassroomScoreDiagnosticResultsUseCase } from '@/core/useCases/dashboard/FetchDashboardClassroomScoreDiagnosticResultsUseCase.ts'
import { FetchClassroomDiagnosticResultsUseCase } from '@/core/useCases/dashboard/FetchClassroomDiagnosticResultsUseCase.ts'
import { FetchClassroomModuleResultsUseCase } from '@/core/useCases/dashboard/FetchClassroomModuleResultsUseCase.ts'
import type { DashboardScoreModule } from '@/core/domain/dashboard/DashboardScoreModule.ts'
import type { DashboardScoreDiagnostic } from '@/core/domain/dashboard/DashboardScoreDiagnostic.ts'
import type {
  DashboardClassroomStudentResult,
} from '@/core/domain/dashboard/DashboardClassroomStudentResult'
import type { ClassroomDiagnosticStudentResult } from '@/core/domain/dashboard/ClassroomDiagnosticStudentResult'
import type { DashboardClassroomModuleResult } from '@/core/domain/dashboard/DashboardClassroomModuleResult.ts'

export const useDashboardClassroomResultStore = defineStore('dashboardClassroomResult', () => {
  // BASE COMMUNE
  const baseStore = createStoreBase([
    'fetchScoreDiagnostic',
    'fetchScoreModule',
    'fetchClassroomDiagnosticResults',
    'fetchClassroomModuleResults'
  ])

  // STORES
  const dashboardStore = useDashboardStore()
  const dashboardClassroomStore = useDashboardClassroomStore()

  const { dashboardSelectedSchool } = storeToRefs(dashboardStore)
  const { dashboardSelectedClassroom } = storeToRefs(dashboardClassroomStore)

  // ÉTAT
  const scoreDiagnostic: Ref<DashboardScoreDiagnostic[]> = ref([])
  const scoreModule: Ref<Record<number, DashboardScoreModule[]>> = ref({})

  // Nouveaux états pour les résultats basés sur la modularisation
  const classroomDiagnosticResults: Ref<ClassroomDiagnosticStudentResult[]> = ref([])
  const classroomModuleResults: Ref<DashboardClassroomModuleResult[]> = ref([])

  // ACTIONS
  /**
   * Récupère les résultats du diagnostic pour une classe
   * @param courseId ID du parcours
   */
  async function fetchSchoolClassroomScoreDiagnostic(
    courseId: number
  ): Promise<DashboardScoreDiagnostic[] | null> {
    try {
      baseStore.loading.value.fetchScoreDiagnostic = true
      baseStore.resetError()

      // Vérifier si les données sont déjà chargées et récentes
      if (
        baseStore.lastUpdated.value &&
        Date.now() - baseStore.lastUpdated.value.getTime() < 5 * 60 * 1000 && // 5 minutes
        scoreDiagnostic.value.length > 0
      ) {
        return scoreDiagnostic.value
      }

      console.log('Fetching score diagnostic:', dashboardSelectedClassroom.value)
      const classroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      const schoolId = dashboardSelectedSchool.value?.id
      const periodId = dashboardSelectedClassroom.value?.schoolPeriodId

      if (!schoolId || !classroomId || !courseId || !periodId) {
        console.log(classroomId, schoolId, periodId, courseId)
        return Promise.reject(
          new Error(
            `Informations manquantes pour récupérer les scores du diagnostic. SchoolId: ${schoolId}, schoolPeriodId: ${periodId}, classroomId: ${classroomId}, courseId: ${courseId} `
          )
        )
      }

      const result = await DependencyContainer.resolve(
        FetchDashboardClassroomScoreDiagnosticResultsUseCase
      ).execute(schoolId, periodId, classroomId, courseId)

      if (result) {
        scoreDiagnostic.value = result
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la récupération des scores de diagnostic:', err)
      return null
    } finally {
      baseStore.loading.value.fetchScoreDiagnostic = false
    }
  }

  /**
   * Récupère les résultats du module pour une classe
   * @param taskId ID de la tâche
   */
  async function fetchSchoolClassroomScoreModule(
    taskId: number
  ): Promise<DashboardScoreModule[] | null> {
    try {
      baseStore.loading.value.fetchScoreModule = true
      baseStore.resetError()

      // Vérifier si les données sont déjà chargées et récentes
      if (
        baseStore.lastUpdated.value &&
        Date.now() - baseStore.lastUpdated.value.getTime() < 5 * 60 * 1000 && // 5 minutes
        scoreModule.value[taskId]?.length > 0
      ) {
        return scoreModule.value[taskId]
      }

      const classroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      const schoolId = dashboardSelectedSchool.value?.id
      const periodId = dashboardSelectedClassroom.value?.schoolPeriodId

      if (!schoolId || !classroomId || !taskId || !periodId) {
        return Promise.reject(
          new Error('Informations manquantes pour récupérer les scores du module')
        )
      }

      const result = await DependencyContainer.resolve(
        FetchDashboardClassroomScoreModuleResultsUseCase
      ).execute(schoolId, periodId, classroomId, taskId)

      if (result) {
        scoreModule.value = {
          ...scoreModule.value,
          [taskId]: result
        }
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la récupération des scores du module:', err)
      return null
    } finally {
      baseStore.loading.value.fetchScoreModule = false
    }
  }

  /**
   * Récupère les résultats de diagnostic d'une classe (nouvelle route API)
   */
  async function fetchClassroomDiagnosticResults(
    forceRefresh = false
  ): Promise<ClassroomDiagnosticStudentResult[] | null> {
    try {
      baseStore.loading.value.fetchClassroomDiagnosticResults = true
      baseStore.resetError()

      // Éviter le rechargement inutile si les données sont déjà là et récentes
      if (
        !forceRefresh &&
        baseStore.lastUpdated.value &&
        Date.now() - baseStore.lastUpdated.value.getTime() < 5 * 60 * 1000 && // 5 minutes
        classroomDiagnosticResults.value.length > 0
      ) {
        return classroomDiagnosticResults.value
      }

      const classroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      const schoolId = dashboardSelectedSchool.value?.id
      const periodId = dashboardSelectedClassroom.value?.schoolPeriodId

      if (!schoolId || !classroomId || !periodId) {
        return Promise.reject(
          new Error('Informations manquantes pour récupérer les résultats de diagnostic')
        )
      }

      const result = await DependencyContainer.resolve(
        FetchClassroomDiagnosticResultsUseCase
      ).execute(schoolId, periodId, classroomId)

      if (result) {
        classroomDiagnosticResults.value = result
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la récupération des résultats de diagnostic:', err)
      return null
    } finally {
      baseStore.loading.value.fetchClassroomDiagnosticResults = false
    }
  }

  /**
   * Récupère les résultats de modules d'une classe (nouvelle route API)
   */
  async function fetchClassroomModuleResults(
    forceRefresh = false
  ): Promise<DashboardClassroomModuleResult[] | null> {
    try {
      baseStore.loading.value.fetchClassroomModuleResults = true
      baseStore.resetError()

      // Éviter le rechargement inutile si les données sont déjà là et récentes
      if (
        !forceRefresh &&
        baseStore.lastUpdated.value &&
        Date.now() - baseStore.lastUpdated.value.getTime() < 5 * 60 * 1000 && // 5 minutes
        classroomModuleResults.value.length > 0
      ) {
        return classroomModuleResults.value
      }

      const classroomId = dashboardSelectedClassroom.value?.schoolClassroomId
      const schoolId = dashboardSelectedSchool.value?.id
      const periodId = dashboardSelectedClassroom.value?.schoolPeriodId

      if (!schoolId || !classroomId || !periodId) {
        return Promise.reject(
          new Error('Informations manquantes pour récupérer les résultats de modules')
        )
      }

      const result = await DependencyContainer.resolve(FetchClassroomModuleResultsUseCase).execute(
        schoolId,
        periodId,
        classroomId
      )

      if (result) {
        classroomModuleResults.value = result
        baseStore.lastUpdated.value = new Date()
      }

      return result
    } catch (err) {
      baseStore.setError(err)
      console.error('Erreur lors de la récupération des résultats de modules:', err)
      return null
    } finally {
      baseStore.loading.value.fetchClassroomModuleResults = false
    }
  }

  /**
   * Obtient les résultats d'un module spécifique à partir des résultats de modules
   */
  function getModuleResults(moduleId: number): DashboardClassroomModuleResult | undefined {
    return classroomModuleResults.value.find((module) => module.learningModuleId === moduleId)
  }

  /**
   * Obtient les résultats d'un élève pour un module spécifique
   */
  function getStudentModuleResult(
    studentId: string,
    moduleId: number
  ): DashboardClassroomStudentResult | undefined {
    const module = classroomModuleResults.value.find((m) => m.learningModuleId === moduleId)
    if (!module) return undefined

    return module.scores.find((score: DashboardClassroomStudentResult) => score.identifier === studentId)
  }

  /**
   * Obtient les résultats de diagnostic pour un étudiant spécifique
   */
  function getStudentDiagnosticResult(
    studentId: string
  ): ClassroomDiagnosticStudentResult | undefined {
    return classroomDiagnosticResults.value.find((student) => student.identifier === studentId)
  }

  /**
   * Obtient les résultats d'une tâche de diagnostic spécifique pour un étudiant
   */
  function getStudentDiagnosticTaskResult(studentId: string, taskId: number) {
    const student = getStudentDiagnosticResult(studentId)
    return student?.getTaskResult(taskId)
  }

  /**
   * Obtient les statistiques globales des diagnostics
   */
  function getDiagnosticStats() {
    const totalStudents = classroomDiagnosticResults.value.length
    const studentsWithDiagnostic = classroomDiagnosticResults.value.filter((s) =>
      s.hasStartedAnyDiagnostic()
    ).length
    const studentsWithEndDiagnostic = classroomDiagnosticResults.value.filter((s) =>
      s.hasCompletedAnyEndDiagnostic()
    ).length

    return {
      totalStudents,
      studentsWithDiagnostic,
      studentsWithEndDiagnostic,
      percentageStarted:
        totalStudents > 0 ? Math.round((studentsWithDiagnostic / totalStudents) * 100) : 0,
      percentageCompleted:
        totalStudents > 0 ? Math.round((studentsWithEndDiagnostic / totalStudents) * 100) : 0
    }
  }

  /**
   * Réinitialise l'état du store
   */
  function reset(): void {
    scoreDiagnostic.value = []
    scoreModule.value = {}
    classroomDiagnosticResults.value = []
    classroomModuleResults.value = []
    baseStore.lastUpdated.value = null
    baseStore.resetError()

    baseStore.loading.value = {
      fetchScoreDiagnostic: false,
      fetchScoreModule: false,
      fetchClassroomDiagnosticResults: false,
      fetchClassroomModuleResults: false
    }
  }

  // GETTERS
  /**
   * Indique si des données sont en cours de chargement
   */
  const isLoading = computed(() => Object.values(baseStore.loading.value).some(Boolean))

  /**
   * Indique si les données doivent être rechargées
   */
  const needsFreshData = computed(() => {
    if (!baseStore.lastUpdated.value) return true

    // Considérer les données comme périmées après 5 minutes
    const fiveMinutesInMs = 5 * 60 * 1000
    return Date.now() - baseStore.lastUpdated.value.getTime() > fiveMinutesInMs
  })

  return {
    // État
    scoreDiagnostic,
    scoreModule,
    classroomDiagnosticResults,
    classroomModuleResults,
    dashboardClassroomResultStoreLoading: baseStore.loading,
    dashboardClassroomResultStoreError: baseStore.error,
    baseStoreLastUpdated: baseStore.lastUpdated,

    // Actions
    fetchSchoolClassroomScoreDiagnostic,
    fetchSchoolClassroomScoreModule,
    fetchClassroomDiagnosticResults,
    fetchClassroomModuleResults,
    getModuleResults,
    getStudentModuleResult,
    getStudentDiagnosticResult,
    getStudentDiagnosticTaskResult,
    getDiagnosticStats,
    reset,

    // Getters
    dashboardClassroomResultStoreIsLoading: isLoading,
    needsFreshData
  }
})
